import { useState, useEffect } from 'react';
import { database } from '../services/firebase';
import { ref, onValue, off } from 'firebase/database';

/**
 * Hook para leer el stock por sucursal desde Firebase RTDB
 * Los datos son sincronizados desde DynamiaERP vía syncCompleteInventory
 */
export function useBranchStock() {
  const [stockByBranch, setStockByBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    console.log('[useBranchStock] Subscribing to /branch_stock...');

    const stockRef = ref(database, 'branch_stock');
    const metadataRef = ref(database, 'inventory_sync_metadata');

    // Subscribe to stock data
    const unsubscribeStock = onValue(
      stockRef,
      (snapshot) => {
        try {
          const data = snapshot.val();
          console.log('[useBranchStock] Stock data loaded:', {
            branches: data ? Object.keys(data) : [],
            totalBranches: data ? Object.keys(data).length : 0
          });
          setStockByBranch(data);
          setLoading(false);
        } catch (err) {
          console.error('[useBranchStock] Error processing stock data:', err);
          setError(err.message);
          setLoading(false);
        }
      },
      (err) => {
        console.error('[useBranchStock] Error subscribing to stock data:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    // Subscribe to metadata
    const unsubscribeMetadata = onValue(
      metadataRef,
      (snapshot) => {
        const data = snapshot.val();
        console.log('[useBranchStock] Metadata loaded:', data);
        setMetadata(data);
      },
      (err) => {
        console.warn('[useBranchStock] Could not load metadata:', err);
      }
    );

    // Cleanup
    return () => {
      console.log('[useBranchStock] Unsubscribing from /branch_stock');
      off(stockRef);
      off(metadataRef);
    };
  }, []);

  /**
   * Get stock for a specific branch
   * @param {string} branchName - Sanitized branch name
   * @returns {Object} - Object with productId as key and stock data as value
   */
  const getStockForBranch = (branchName) => {
    if (!stockByBranch || !branchName) return {};
    return stockByBranch[branchName] || {};
  };

  /**
   * Get stock for a specific product in a specific branch
   * @param {string} branchName - Sanitized branch name
   * @param {string} productId - Product ID
   * @returns {Object|null} - Stock data object or null
   */
  const getProductStock = (branchName, productId) => {
    if (!stockByBranch || !branchName || !productId) return null;
    return stockByBranch[branchName]?.[productId] || null;
  };

  /**
   * Get all available branch names
   * @returns {Array<string>} - Array of sanitized branch names
   */
  const getBranchNames = () => {
    if (!stockByBranch) return [];
    return Object.keys(stockByBranch);
  };

  /**
   * Get product details across all branches
   * @param {string} productId - Product ID
   * @returns {Object} - Object with branch names as keys and stock data as values
   */
  const getProductAcrossBranches = (productId) => {
    if (!stockByBranch || !productId) return {};

    const result = {};
    Object.entries(stockByBranch).forEach(([branchName, products]) => {
      if (products[productId]) {
        result[branchName] = products[productId];
      }
    });
    return result;
  };

  return {
    stockByBranch,
    loading,
    error,
    metadata,
    getStockForBranch,
    getProductStock,
    getBranchNames,
    getProductAcrossBranches
  };
}
